/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useContext, useMemo, useState } from 'react';
import Subscription from '../../../domain/models/Subscription';
import UserDoc from '../../../domain/models/UserDoc';
import getSubscriptionById from '../../../domain/use_cases/subscriptions/getSubscriptionById';
import getUsersBySubscriptionId from '../../../domain/use_cases/users/getUsersBySubscriptionId';
import searchContacts from '../../../domain/use_cases/contacts/searchContacts';
import Scenario from '../../../domain/models/Scenario';
import getScenariosByIds from '../../../domain/use_cases/scenarios/getScenariosByIds';
import AuthenticationContext from '../auth/AuthenticationContext';

import { IContact } from '@leedpulse-monorepo/interfaces';
import { ContextStatus, ESmsRole } from '@leedpulse-monorepo/enums';

const SubscriptionChatsContext = createContext({
  subscription: null as Subscription | null,
  subscriptionOwners: [] as UserDoc[],
  subscriptionScenarios: [] as Scenario[],
  constrainedByFilter: null as SubscriptionChatsFilter[] | null,
  contacts: [] as IContact[],
  contactsCount: 0,
  nbPagesLoaded: 0,
  contactsFetchingStatus: ContextStatus.IDLE,
  selectedContact: null as IContact | null,
  initWithSubscriptionId: (_: string) => { },
  initializationStatus: ContextStatus.IDLE,
  selectContact: (_: IContact) => { },
  noSearchResults: false,
  search: (_: string, __?: number, ___?: SubscriptionChatsFilter[]) => { },
  resetSearch: () => { },
  contactsFilteringStatus: ContextStatus.IDLE,
  reset: () => { },
  onFilterChange: (_: string, __: SubscriptionChatsFilter[]) => { },
  loadMoreContacts: () => { },
  onAllMessagesReading: () => { },
  onOneMessageReadStatusChange: (becomeRead: boolean, role: ESmsRole) => { },
});

export const SUBSCRIPTION_CHATS_FETCH_LIMIT = 8;

export type SubscriptionChatsFilter = {
  type: 'qualification_ai' | 'prospect_category' | 'owner';
  value: string;
}

export const SubscriptionChatsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const authContext = useContext(AuthenticationContext);

  const [subscription, setSubscription] = useState(null as Subscription | null);
  const [subscriptionOwners, setSubscriptionOwners] = useState([] as UserDoc[]);
  const [subscriptionScenarios, setSubscriptionScenarios] = useState([] as Scenario[]);
  const [constrainedByFilter, setConstrainedByFilter] = useState(
    null as SubscriptionChatsFilter[] | null
  );
  const [baseContacts, setBaseContacts] = useState<IContact[]>([]);
  const [contactsCount, setContactsCount] = useState(0);
  const [searchResults, setSearchResults] = useState<IContact[]>([]);
  const [noSearchResults, setNoSearchResults] = useState(false);
  const [contactsFilteringStatus, setContactsFilteringStatus] = useState(
    ContextStatus.IDLE
  );
  const [nbPagesLoaded, setNbPagesLoaded] = useState<number>(0);
  const [contactsFetchingStatus, setContactsFetchingStatus] = useState(
    ContextStatus.IDLE
  );
  const [selectedContact, setSelectedContact] = useState(
    null as IContact | null
  );

  const [initializationStatus, setInitializationStatus] = useState(
    ContextStatus.IDLE
  );

  const contacts = useMemo(() => {
    return searchResults.length > 0 ? searchResults : baseContacts;
  }, [searchResults, baseContacts]);

  const initWithSubscriptionId = async (subscriptionId: string) => {
    setInitializationStatus(ContextStatus.LOADING);
    try {
      const subscription = await getSubscriptionById(subscriptionId);
      if (subscription === null) {
        throw new Error('Subscription not found');
      }
      setSubscription(subscription?.subscription);
      const owners = await getUsersBySubscriptionId(subscriptionId);
      const scenarios = await getScenariosByIds(subscription.subscription.idScenarios || []);
      setSubscriptionOwners(owners);
      setSubscriptionScenarios(scenarios);

      const result = await searchContacts(
        authContext.authentication,
        '',
        0,
        SUBSCRIPTION_CHATS_FETCH_LIMIT,
        {
          apiKey: subscription.subscription.apiKey,
        }
      );
      setBaseContacts(result.hits);
      setContactsCount(result.nbHits);
      setNbPagesLoaded(result.page);
      if (result.hits.length > 0) {
        setSelectedContact(result.hits[0]);
      }
      setInitializationStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setInitializationStatus(ContextStatus.ERROR);
    }
  };

  const onFilterChange = async (searchQuery: string, filters: SubscriptionChatsFilter[]) => {
    setConstrainedByFilter(filters);
    setContactsFilteringStatus(ContextStatus.LOADING);

    try {
      search(
        searchQuery,
        0,
        filters === null || filters.length === 0 ? undefined : filters
      );

      setContactsFilteringStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setContactsFilteringStatus(ContextStatus.ERROR);
    }
  };

  const selectContact = (contact: IContact) => {
    setSelectedContact(contact);
  };

  const loadMoreContacts = async () => {
    setContactsFetchingStatus(ContextStatus.LOADING);
    try {
      const result = await searchContacts(
        authContext.authentication,
        '',
        nbPagesLoaded + 1,
        SUBSCRIPTION_CHATS_FETCH_LIMIT,
        {
          apiKey: subscription?.apiKey || undefined,
        }
      );

      setBaseContacts(o => [...o, ...result.hits]);
      setContactsCount(result.nbHits);
      setNbPagesLoaded(result.page);
      setContactsFetchingStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setContactsFetchingStatus(ContextStatus.ERROR);
    }
  };

  const search = async (searchQuery: string, page = 0, filters?: SubscriptionChatsFilter[]) => {
    setContactsFilteringStatus(ContextStatus.LOADING);
    try {
      const mFilters = new Map<string, string>();
      filters?.forEach(filter => {
        const currentFilter = mFilters.get(filter.type);
        if (currentFilter)
          mFilters.set(filter.type, currentFilter + ';' + filter.value);
        else
          mFilters.set(filter.type, filter.value);
      });

      const useCaseResponse = await searchContacts(
        authContext.authentication,
        searchQuery,
        page,
        SUBSCRIPTION_CHATS_FETCH_LIMIT,
        {
          apiKey: subscription?.apiKey || undefined,
          ...Object.fromEntries(mFilters),
        }
      );
      setSearchResults((o) => {
        const newHits = useCaseResponse.hits.map(hit => hit);
        return page === 0 ? newHits : [...o, ...newHits]; // If more than page 0, add
      });
      setContactsCount(useCaseResponse.nbHits);
      setNoSearchResults(useCaseResponse.hits.length === 0); // Ce state est archi stupide
      setNbPagesLoaded(useCaseResponse.page);
      setContactsFilteringStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setContactsFilteringStatus(ContextStatus.ERROR);
    }
  };

  const resetSearch = () => {
    setSearchResults([]);
    setNoSearchResults(false);
  };

  const reset = () => {
    setSubscription(null);
    setSubscriptionOwners([]);
    setBaseContacts([]);
    setSearchResults([]);
    setNoSearchResults(false);
    setSelectedContact(null);

    setContactsCount(0);
    setNbPagesLoaded(0);
    setInitializationStatus(ContextStatus.IDLE);

    setContactsFetchingStatus(ContextStatus.IDLE);
    setContactsFilteringStatus(ContextStatus.IDLE);
  };

  const onAllMessagesReading = () => {
    if (selectedContact === null) {
      return;
    }
    const newContacts = baseContacts.map((contact) => {
      if (contact.id === selectedContact.id) {
        contact.messages_counters.backoffice = {
          assistant: {
            read: 0,
            unread: 0,
          },
          user: {
            read: 0,
            unread: 0,
          },
        };
      }
      return contact;
    });
    setBaseContacts(newContacts);
  };

  const onOneMessageReadStatusChange = (becomeRead: boolean, role: ESmsRole) => {
    if (selectedContact === null) {
      return;
    }
    const newContacts = baseContacts.map((contact) => {
      if (contact.id === selectedContact.id) {
        return {
          ...contact,
          messages_counters: {
            ...contact.messages_counters,
            backoffice: {
              ...contact.messages_counters.backoffice,
              [role]: {
                ...contact.messages_counters.backoffice[role],
                unread: becomeRead
                  ? (contact.messages_counters.backoffice[role]?.unread ?? 0) - 1
                  : (contact.messages_counters.backoffice[role]?.unread ?? 0) + 1,
                read: becomeRead
                  ? (contact.messages_counters.backoffice[role]?.read ?? 0) + 1
                  : (contact.messages_counters.backoffice[role]?.read ?? 0) - 1,
              },
            },
          },
        }
      }
      return contact;
    });
    setBaseContacts(newContacts);
  };

  return (
    <SubscriptionChatsContext.Provider
      value={{
        subscription,
        subscriptionOwners,
        subscriptionScenarios,
        constrainedByFilter,
        contacts,
        contactsCount,
        noSearchResults,
        search,
        resetSearch,
        contactsFilteringStatus,
        nbPagesLoaded,
        selectedContact,
        contactsFetchingStatus,
        initWithSubscriptionId,
        initializationStatus,
        selectContact,
        reset,
        onFilterChange,
        loadMoreContacts,
        onAllMessagesReading,
        onOneMessageReadStatusChange,
      }}
    >
      {children}
    </SubscriptionChatsContext.Provider>
  );
};

export default SubscriptionChatsContext;
